// A rating, whether they have seen the artist, and what a note actually meant
// are three different facts. Collapsing them into one number is how a sentence
// becomes an inferred vote nobody cast.
//
// A tag counts only once its author has confirmed it. An unconfirmed suggestion
// is a question on a card, never a weight in the solver, and never a diagnosis.
//
// Having seen an artist costs that artist nothing by itself. Only a discovery
// weight the person turned on can move it, in whichever direction they chose.
// Somebody who goes to see the same act every year is not making a mistake.
//
// These weights are product defaults to calibrate against real crews, not
// measurements of anything.

export const EMOTIONS = new Set([
  'nostalgic',
  'uplifting',
  'cathartic',
  'comforting',
  'curious',
  'overwhelming',
  'meaningful',
]);

export type SeenBefore = 'yes' | 'no' | 'unknown';

export interface EmotionTag {
  name: string;
  intensity: number;
  confirmed: boolean;
}

export interface ArtistPreference {
  score: number | null;
  seenBefore: SeenBefore;
  note: string;
  tags: EmotionTag[];
}

export interface PreferenceValue {
  value: number | null;
  reason?: string;
  components: { kind: string; value: number }[];
}

export function validatePreference(p: ArtistPreference): ArtistPreference {
  if (!p || !(p.score === null || (Number.isInteger(p.score) && p.score >= 1 && p.score <= 10))) {
    throw new Error('score');
  }
  if (!['yes', 'no', 'unknown'].includes(p.seenBefore)) throw new Error('seenBefore');
  if (typeof p.note !== 'string' || p.note.length > 2000) throw new Error('note');
  if (!Array.isArray(p.tags) || p.tags.length > 12) throw new Error('tags');

  const seen = new Set<string>();
  for (const tag of p.tags) {
    if (
      !EMOTIONS.has(tag.name) ||
      seen.has(tag.name) ||
      !Number.isInteger(tag.intensity) ||
      tag.intensity < 1 ||
      tag.intensity > 3 ||
      typeof tag.confirmed !== 'boolean'
    ) {
      throw new Error('tag');
    }
    seen.add(tag.name);
  }
  return p;
}

export function preferenceValue(
  p: ArtistPreference,
  { noveltyWeight = 0, emotionalWeight = 0.15 }: { noveltyWeight?: number; emotionalWeight?: number } = {},
): PreferenceValue {
  validatePreference(p);
  if (
    !Number.isFinite(noveltyWeight) ||
    Math.abs(noveltyWeight) > 0.1 ||
    !Number.isFinite(emotionalWeight) ||
    emotionalWeight < 0 ||
    emotionalWeight > 0.2
  ) {
    throw new Error('weights');
  }
  if (p.score === null) return { value: null, reason: 'unrated', components: [] };

  const base = (p.score - 1) / 9;
  const novelty = p.seenBefore === 'no' ? noveltyWeight : 0;

  // The strongest confirmed resonance, not the sum. Adding them rewards writing
  // a longer note rather than caring more.
  const values = p.tags
    .filter((t) => t.confirmed)
    .map((t) => ((t.name === 'overwhelming' ? -1 : 1) * t.intensity) / 3);
  const resonance = values.length ? values.reduce((a, b) => (Math.abs(b) > Math.abs(a) ? b : a), 0) : 0;
  const emotion = emotionalWeight * resonance;

  return {
    value: Math.max(0, Math.min(1, base + novelty + emotion)),
    components: [
      { kind: 'rating', value: base },
      { kind: 'discovery', value: novelty },
      { kind: 'confirmed_resonance', value: emotion },
    ],
  };
}

// Requires the suggestion's note revision to match the note's own, and its
// evidence span to fall inside that note. A tag cannot attach to words the note
// does not contain.
export function confirmTag(
  proposal: {
    noteRevision: number;
    name: string;
    intensity: number;
    evidence?: { start: number; end: number };
  },
  note: string,
  currentRevision: number,
): EmotionTag {
  if (proposal.noteRevision !== currentRevision) throw new Error('stale_note');
  const { start, end } = proposal.evidence ?? {};
  if (
    !Number.isInteger(start) ||
    !Number.isInteger(end) ||
    start! < 0 ||
    end! <= start! ||
    end! > note.length
  ) {
    throw new Error('evidence');
  }
  if (
    !EMOTIONS.has(proposal.name) ||
    !Number.isInteger(proposal.intensity) ||
    proposal.intensity < 1 ||
    proposal.intensity > 3
  ) {
    throw new Error('tag');
  }
  return { name: proposal.name, intensity: proposal.intensity, confirmed: true };
}
